import React, { useEffect, useState } from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { Check } from "lucide-react";
import { Select, SelectContent, SelectTrigger, SelectValue } from "../ui/select";
import { api } from "../../lib/api";

function IndustryOption({ code, name, description, supported }) {
  return (
    <SelectPrimitive.Item
      value={code}
      data-testid={`industry-option-${code}`}
      className="relative flex cursor-pointer select-none flex-col rounded-sm py-2 pl-8 pr-2 outline-none focus:bg-surface data-[state=checked]:bg-surface data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
    >
      <span className="absolute left-2 top-2.5 flex h-3.5 w-3.5 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <Check className="h-4 w-4" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <span className="flex items-center gap-2">
        <SelectPrimitive.ItemText>
          <span className="font-mono text-[11px] text-muted-foreground">{code}</span>
          <span className="ml-2 text-sm">{name}</span>
        </SelectPrimitive.ItemText>
        {supported && (
          <span
            className="ml-auto shrink-0 rounded-full bg-accent/15 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-accent"
            data-testid={`sustek-badge-${code}`}
          >
            SusTek-supported
          </span>
        )}
      </span>
      {description && (
        <span className="mt-0.5 text-[10px] leading-tight text-muted-foreground">{description}</span>
      )}
    </SelectPrimitive.Item>
  );
}

// Grouped, sector-by-sector SASB/SICS industry chooser with a "SusTek-supported"
// badge on industries that have built-in Lestari coverage. Saves the industry code.
export function IndustrySelect({ value, onChange, testId = "industry-select", placeholder = "Select your industry…" }) {
  const [industries, setIndustries] = useState([]);
  const [sectors, setSectors] = useState([]);

  useEffect(() => {
    api.get("/tenants/industries").then((r) => {
      setIndustries(r.data.industries || []);
      setSectors(r.data.sectors || []);
    }).catch(() => {});
  }, []);

  // Fall back to sector order derived from the industry list if the API omits it.
  const groups = sectors.length
    ? sectors
    : [...new Set(industries.map((i) => i.sector))].map((name) => ({ name, code: "" }));

  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testId}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="max-h-[22rem]">
        {groups.map((s) => {
          const items = industries.filter((i) => i.sector === s.name);
          if (!items.length) return null;
          return (
            <SelectPrimitive.Group key={s.code || s.name}>
              <SelectPrimitive.Label className="flex items-center gap-1.5 px-2 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-wider text-foreground/70">
                {s.code && <span className="font-mono text-accent">{s.code}</span>}
                <span>{s.name}</span>
              </SelectPrimitive.Label>
              {items.map((i) => (
                <IndustryOption key={i.code} {...i} />
              ))}
            </SelectPrimitive.Group>
          );
        })}
      </SelectContent>
    </Select>
  );
}
